import React from 'react';
import type { ExpertService } from '../../types';

interface ServiceCardProps {
  service: ExpertService;
  isSelected: boolean;
  onSelect: () => void;
}

export const ServiceCard: React.FC<ServiceCardProps> = ({ service, isSelected, onSelect }) => {
  const formatPrice = (cents: number, currency: string) => {
    const amount = cents / 100;
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);
  };

  return (
    <button
      onClick={onSelect}
      aria-pressed={isSelected}
      className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
        isSelected
          ? 'border-purple-500 bg-purple-50 shadow-md'
          : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
      }`}
    >
      {/* Service name and price row */}
      <div className="flex items-start justify-between gap-3">
        <h3 className="font-semibold text-gray-900 flex-1">{service.name}</h3>
        <div className="text-right flex-shrink-0">
          <div className="font-semibold text-lg text-gray-900">
            {formatPrice(service.price_cents, service.currency)}
          </div>
          {isSelected && (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 mt-1">
              Selected
            </span>
          )}
        </div>
      </div>

      {/* Description - full width */}
      {service.description && (
        <p className="text-sm text-gray-600 mt-2 line-clamp-2">{service.description}</p>
      )}

      {/* Duration and deposit row */}
      <div className="flex items-center justify-between mt-3 text-sm text-gray-500">
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            {service.duration_min} min
          </span>
          {(service.padding_before_min > 0 || service.padding_after_min > 0) && (
            <span className="text-xs text-gray-400">
              +{service.padding_before_min + service.padding_after_min} min buffer
            </span>
          )}
        </div>
        {service.deposit_cents > 0 && (
          <span className="text-xs text-gray-500">
            Deposit: {formatPrice(service.deposit_cents, service.currency)}
          </span>
        )}
      </div>
    </button>
  );
};

interface ServiceListProps {
  services: ExpertService[];
  selectedServiceId: string | null;
  onSelectService: (serviceId: string) => void;
}

export const ServiceList: React.FC<ServiceListProps> = ({
  services,
  selectedServiceId,
  onSelectService,
}) => {
  if (services.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <p className="text-gray-500 text-center">No services available</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h2 className="text-xl font-bold text-gray-900 mb-4">Select a Service</h2>
      <div className="space-y-3">
        {services.map((service) => (
          <ServiceCard
            key={service.id}
            service={service}
            isSelected={service.id === selectedServiceId}
            onSelect={() => onSelectService(service.id)}
          />
        ))}
      </div>
    </div>
  );
};
